<template>
  <v-navigation-drawer
    v-model="glossaryStore.isOpen"
    temporary
    :location="display.mobile.value ? 'bottom' : 'right'"
    width="400"
  >
    <v-toolbar density="compact" bg-color="transparent" class="py-0">
      <v-toolbar-title class="mt-15">Glossary</v-toolbar-title>
      <div class="close-btn">
        <span
          class="text-caption mr-2 close-text text-uppercase"
          @click="glossaryStore.closeGlossary"
          >Close</span
        >
        <v-btn icon bg-color="primary" @click="glossaryStore.closeGlossary">
          <v-icon color="white" size="20">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-toolbar>

    <v-container class="pa-4">
      <div class="d-flex align-center justify-space-between">
        <div class="d-flex search-container">
          <v-text-field
            density="compact"
            variant="outlined"
            placeholder="Search"
            hide-details
            class="search-field"
          ></v-text-field>
          <v-btn icon variant="text" color="primary" class="search-btn">
            <v-icon color="white">mdi-magnify</v-icon>
          </v-btn>
        </div>

        <v-select
          :items="['A-Z', 'Z-A']"
          variant="outlined"
          :hide-details="true"
          density="compact"
          class="small-select"
          append-inner-icon="mdi-chevron-down"
          :menu-props="{ contentClass: 'lighter-surface' }"
          v-model="selectedSortOption"
        />
      </div>

      <v-list lines="two" class="pa-0 mt-6">
        <template v-for="section in glossaryItems" :key="section.letter">
          <div class="glossary-item mb-4">
            <div class="letter-header mb-4 pl-4">
              <h1 class="text-secondary">{{ section.letter }}</h1>
            </div>
            <v-list-item
              v-for="(item, index) in section.items"
              :key="index"
              :title="item.term"
              :subterm="item.subterm"
              :class="['mb-8 rounded', { 'glossary-item-active': item.active }]"
              variant="outlined"
            >
              <div class="d-flex flex-column">
                <div
                  v-html="
                    typeof item.definition === 'function'
                      ? item.definition(item.subterm)
                      : item.definition
                  "
                ></div>
                <v-btn
                  v-if="item.active"
                  variant="text"
                  append-icon="mdi-chevron-right"
                  @click="viewDefinition"
                  class="glossary-btn align-self-start"
                >
                  Learn More
                </v-btn>
              </div>
            </v-list-item>
          </div>
        </template>
      </v-list>
    </v-container>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
  import { useDisplay } from 'vuetify';
  import { useGlossaryStore } from '../../store';
  import { ref } from 'vue';

  const display = useDisplay();
  const glossaryStore = useGlossaryStore();

  const viewDefinition = () => {
    // Handle view full definition action (currently does nothing)
  };

  // Mock data - replace with actual data from store/API
  const glossaryItems = [
    {
      letter: 'E',
      items: [
        {
          term: 'ELA Format',
          subterm: 'ELA K-2',
          active: true,
          definition: (subterm: string) => `
            <div class="text-caption pt-3" style="color: rgb(var(--v-theme-secondary)) !important;">${subterm}</div>
            <ul class="glossary-card-list">
              <li>
              <div class="li-container"><h4>Core: Comprehensive</h4><p>Instructional materials that claim to deliver all grade-level or course level standards</p></div></li>
              <li><div class="li-container"><h4>Core: No Foundational Skills</h4><p>Instructional materials designed to be supplemented with foundational skills-specific products</p></div></li></li>
              <li><div class="li-container"><h4>Supplemental: Foundational Skills Only</h4><p>Instructional materials designed to only deliver foundational skills content and to be used with a core curriculum</p></div></li></li>
            </ul>
          `,
        },
      ],
    },
    {
      letter: 'F',
      items: [
        {
          term: 'Fluency',
          subterm: 'ELA K-2',
          active: false,
          definition: (subterm: string) => `
            <div class="text-caption pt-3" style="color: rgb(var(--v-theme-secondary)) !important;">${subterm}</div>
            <ul class="glossary-card-list">
              <li>
              <div class="li-container"><h4>Core: Comprehensive</h4><p>Instructional materials that claim to deliver all grade-level or course level standards</p></div></li>
              <li><div class="li-container"><h4>Core: No Foundational Skills</h4><p>Instructional materials designed to be supplemented with foundational skills-specific products</p></div></li></li>
              <li><div class="li-container"><h4>Supplemental: Foundational Skills Only</h4><p>Instructional materials designed to only deliver foundational skills content and to be used with a core curriculum</p></div></li></li>
            </ul>
          `,
        },
      ],
    },
  ];

  const selectedSortOption = ref('A-Z');
</script>

<style lang="scss" scoped>
  // Variables
  $toolbar-height: 108px;
  $button-size: 32px;
  $search-max-width: 218px;
  $select-width: 74px;

  // Layout
  .close-btn {
    position: absolute;
    right: 25px;
    top: 25px;
    .v-btn {
      background-color: rgb(var(--v-theme-primary)) !important;
      width: 32px !important;
      height: 32px !important;
      min-height: 32px !important;
      min-width: 32px !important;
    }
  }

  .search-container {
    flex: 1;
    position: relative;
    max-width: $search-max-width;
  }

  .letter-header {
    border-left: 3px solid rgb(var(--v-theme-secondary));
    margin-bottom: 1rem;
    padding-left: 1rem;
  }

  // Glossary List Styles
  :deep(.glossary-card-list) {
    list-style-type: disc;
    list-style-position: inside;
    margin-left: 10px;
    li {
      margin: 8px 0 16px 0;
      color: rgb(var(--v-theme-text-secondary));
      font-size: 14px;

      .li-container {
        display: inline;
        margin-left: -10px;

        h4 {
          display: inline;
          margin: 0 0 4px;
          font-size: 14px;
          color: rgb(var(--v-theme-secondary));
          font-weight: bold;
        }

        p {
          margin: 0;
          font-size: 14px;
          color: rgb(var(--v-theme-secondary));
        }
      }
    }
  }

  // Vuetify Component Overrides
  :deep(.v-list-item) {
    background-color: transparent;
    border-color: transparent;
    &.glossary-item-active {
      background-color: white;
      border-color: rgb(var(--v-theme-primary));
    }

    .v-list-item-title {
      font-weight: bold !important;
      color: rgb(var(--v-theme-secondary)) !important;
    }

    .v-btn {
      padding: 10px !important;
      .v-btn--icon {
        background-color: rgb(var(--v-theme-primary)) !important;
      }

      .v-btn__content {
        color: rgb(var(--v-theme-secondary)) !important;
        font-size: 12px !important;
        font-weight: bold !important;
      }
      .v-btn__append {
        margin-inline: 0 !important;
      }
    }
  }

  :deep(.v-toolbar) {
    background-color: transparent !important;
  }

  :deep(.v-toolbar__content) {
    height: auto !important;
    align-items: flex-start !important;
  }

  :deep(.v-toolbar-title) {
    margin-top: 48px !important;
    font-weight: bold !important;
  }

  :deep(.v-icon) {
    font-size: 20px !important;
    width: 20px !important;
    height: 20px !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  // Search Field Styles
  :deep(.search-field) {
    .v-field {
      background-color: white !important;
      border-end-end-radius: 0 !important;
      border-start-end-radius: 0 !important;
      height: $button-size !important;
      min-height: $button-size !important;
    }

    .v-field__outline {
      display: none !important;
    }

    .v-field__field {
      border: 1px solid rgba(var(--v-theme-on-surface), 0.38) !important;
      border-radius: 4px 0 0 4px !important;
      &:before,
      &:after {
        display: none !important;
      }
    }

    .v-field--focused .v-field__field {
      border-color: rgb(var(--v-theme-primary)) !important;
    }

    .v-field__input {
      color: rgb(var(--v-theme-tonal-black)) !important;
      font-size: 12px !important;
      padding: 4px 10px !important;
      min-height: $button-size !important;
      display: flex !important;
      align-items: center !important;
    }
  }

  :deep(.search-btn) {
    background-color: rgb(var(--v-theme-primary)) !important;
    margin: 0 !important;
    height: 34px !important;
    min-height: 34px !important;
    width: $button-size !important;
    min-width: $button-size !important;
    border-radius: 0 4px 4px 0 !important;
  }

  // Select Styles
  .small-select {
    max-width: $select-width;
    background-color: white !important;

    :deep(.v-field) {
      border-color: rgb(var(--v-theme-lighter-grey)) !important;
      min-height: $button-size !important;
      font-size: 12px !important;
    }

    :deep(.v-field__outline) {
      --v-field-border-width: 1px !important;
    }

    :deep(.v-input--density-compact) {
      --v-input-control-height: $button-size;
      --v-input-padding-top: 5px;
    }

    :deep(.v-input__control) {
      height: $button-size;
    }

    :deep(.v-field__input) {
      min-height: 0 !important;
    }

    :deep(.v-field__append-inner .v-icon) {
      transition: transform 0.2s ease;
    }

    :deep(.mdi-chevron-down) {
      color: rgb(var(--v-theme-blue)) !important;
      opacity: 1;
    }

    :deep(.v-text-field--box .v-input__slot),
    :deep(.v-text-field--outline .v-input__slot) {
      min-height: auto !important;
      display: flex !important;
      align-items: center !important;
    }

    :deep(.mdi-menu-down) {
      display: none !important;
    }
  }
</style>
